
import { useCallback, useEffect, useRef, useState } from "react";
import { prefetchPreparedStory } from "@/lib/preparedStory";
import { NewsArticle } from "@/types/news";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Heart, MessageCircle, Share2 } from "lucide-react";
import { cn, stripHtml } from "@/lib/utils";
import { Link } from "@/lib/router-compat";
import ShareArticleModal from "@/components/article/ShareArticleModal";
import { useAuth } from "@/context/AuthContext";
import SmartImage from "@/components/ui/SmartImage";
import { isInternalSource } from "@/lib/internalSource";
import { useArticleHeadline } from "@/context/ArticleTranslationContext";
import { useCategoryLabel } from "@/i18n/useLocale";

export type ArticleCardVariant = "grid" | "row";

interface ArticleCardProps {
  article: NewsArticle;
  /** "grid" = full card used in every list. "row" = compact horizontal card for narrow spaces. */
  variant?: ArticleCardVariant;
  className?: string;
  onToggleBookmark?: (articleId: string) => Promise<void>;
  /** Above-the-fold card: loads its image eagerly at high priority. */
  priority?: boolean;
}

/**
 * The single article card used everywhere in the app (feed, search, topics,
 * bookmarks, markets, weekly brief, daily recap, sidebars).
 */
const ArticleCard = ({ article, variant = "grid", className, priority = false }: ArticleCardProps) => {
  const { isAuthenticated } = useAuth();
  const [showShareModal, setShowShareModal] = useState(false);
  const isRow = variant === "row";

  // On the Norwegian/Chinese editions this swaps in the saved translation.
  const categoryLabel = useCategoryLabel();
  const localized = useArticleHeadline(article.id, { title: article.title, excerpt: article.excerpt });
  const title = localized.title;
  const excerpt = stripHtml(localized.excerpt || "").trim();
  const isPressRelease = article.category === "Press Release";

  // Warm the prepared write-up so the story opens instantly.
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const warmStory = useCallback(() => {
    prefetchPreparedStory(article.id);
  }, [article.id]);

  useEffect(() => {
    const node = linkRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          prefetchPreparedStory(article.id);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [article.id]);

  const rowCard = (
    <Card
      className={cn(
        "overflow-hidden transition-all duration-200",
        "border border-border/40 hover:border-border/80",
        "shadow-card-subtle hover:shadow-md",
        className
      )}
    >
      <CardContent className="flex items-start gap-3 p-3">
        <SmartImage
          src={article.imageUrl}
          alt={title}
          w={128}
          h={96}
          className="h-16 w-24 shrink-0 rounded-lg object-cover"
        />
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug">{title}</h3>
          <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="truncate">
              {isInternalSource(article.source?.id)
                ? article.author?.name || "Byteline"
                : article.source?.name}
            </span>
            {article.readTime && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {article.readTime}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const gridCard = (
    <Card
      className={cn(
        "flex h-full flex-col overflow-hidden transition-all duration-200",
        "border border-border/40 hover:border-border/80",
        "shadow-card-subtle hover:shadow-md",
        "break-inside-avoid",
        className
      )}
    >
      <div className="relative overflow-hidden">
        <SmartImage
          src={article.imageUrl}
          alt={title}
          w={640}
          h={340}
          quality={60}
          sizes="(min-width: 1280px) 420px, (min-width: 768px) 45vw, 92vw"
          widths={[320, 420, 560, 700]}
          priority={priority}
          className="h-48 w-full object-cover transition-transform duration-200 hover:scale-105"
        />

        <div className="absolute right-2 top-2 flex flex-wrap justify-end gap-1">
          {isPressRelease && (
            <Badge variant="outline" className="bg-background/90 text-xs">
              Press Release
            </Badge>
          )}
          <Badge variant="secondary" className="text-xs">
            {isInternalSource(article.source?.id)
              ? article.author?.name || "Byteline"
              : article.source?.name}
          </Badge>
        </div>
      </div>

      <CardContent className="flex flex-1 flex-col p-4">
        <h3 className="mb-2 line-clamp-2 text-base font-semibold leading-tight">{title}</h3>
        {excerpt.length > 20 && (
          <p className="mb-3 line-clamp-3 text-sm text-muted-foreground">{excerpt}</p>
        )}

        <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            {article.author?.name && <span className="max-w-24 truncate">{article.author.name}</span>}
            {article.readTime && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{article.readTime}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Heart className="h-3 w-3" />
              <span>{article.likes_count || 0}</span>
            </div>
            <div className="flex items-center gap-1">
              <MessageCircle className="h-3 w-3" />
              <span>{article.comments_count || 0}</span>
            </div>
            {isAuthenticated && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 hover:bg-transparent"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowShareModal(true);
                }}
              >
                <div className="flex items-center gap-1 transition-colors hover:text-primary">
                  <Share2 className="h-3 w-3" />
                </div>
              </Button>
            )}
          </div>
        </div>

        {article.category && (
          <div className="mt-3 flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {categoryLabel(article.category)}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <>
      <Link
        to={`/article/${article.slug || article.id}`}
        className="group block h-full"
        ref={linkRef}
        onMouseEnter={warmStory}
        onFocus={warmStory}
        onTouchStart={warmStory}
      >
        {isRow ? rowCard : gridCard}
      </Link>
      <ShareArticleModal
        articleId={article.id}
        articleTitle={title}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  );
};

export default ArticleCard;
